import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  HStack,
  VStack,
  Icon,
  Button,
  FormControl,
  FormLabel,
  Input,
  IconButton,
  useToast,
  useToken,
} from '@chakra-ui/react';
import { CheckCircleIcon, CloseIcon } from '@chakra-ui/icons';
import { LuUpload, LuFileText, LuFileSpreadsheet, LuImage } from 'react-icons/lu';
import { useDropzone } from 'react-dropzone';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { processStatement, fetchAccounts, fetchSources } from '../../api/endpointsBackend';
import { getFileType, requiresSource } from '../../utils/statementHelpers';
import { SourceGlyph } from '../accounts/AccountForm';
import { MAX_UPLOAD_FILES, TINT } from './sourceFilesUI';
import ConfirmationDialog from '../shared/ConfirmationDialog';
import { cardSurface } from '../shared/Card';
import useIsPhone from '../../hooks/useIsPhone';
import PhoneUploadSheet from '../phone/PhoneUploadSheet';
import { limitRefusal } from '../../utils/aiUsage';

const FORMAT_CHIPS = [
  { labelKey: 'pdf', icon: LuFileText, color: 'redesign.accent.expense' },
  { labelKey: 'csv', icon: LuFileSpreadsheet, color: 'redesign.accent.green' },
  { labelKey: 'xls', icon: LuFileSpreadsheet, color: 'redesign.accent.blue' },
  { labelKey: 'img', icon: LuImage, color: 'redesign.text.muted' },
];

const ACCEPT = {
  'text/csv': ['.csv'],
  'text/plain': ['.txt'],
  'text/tab-separated-values': ['.tsv'],
  'application/csv': ['.csv'],
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
};

// `defaultAccountId` is onboarding's: the account the user created two steps
// earlier. The uploader otherwise asks for this from scratch, which is a second
// question about something the caller already knows.
export default function SourceFilesUploader({ onUploadStarted, defaultAccountId }) {
  const { t } = useTranslation('sourceFiles');
  const toast = useToast();
  const isPhone = useIsPhone();
  // react-select takes plain CSS values, not theme tokens.
  const mdFontSize = useToken('fontSizes', 'md');
  const [surfaceBg, popupBg, hoverBg, selectedBg, borderHex, textHex, mutedHex, headingHex, strongHex, accentHex] =
    useToken('colors', [
      'redesign.bg.surface',
      'redesign.bg.popup',
      'redesign.bg.hover',
      'redesign.bg.accent',
      'redesign.border',
      'redesign.text',
      'redesign.text.muted',
      'redesign.heading',
      'redesign.border.strong',
      'redesign.accent.blue',
    ]);

  const reactSelectStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: '40px',
        backgroundColor: surfaceBg,
        borderColor: state.isFocused ? accentHex : strongHex,
        boxShadow: state.isFocused ? `0 0 0 1px ${accentHex}` : 'none',
        '&:hover': { borderColor: accentHex },
        fontSize: mdFontSize,
      }),
      menu: (base) => ({
        ...base,
        backgroundColor: popupBg,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: borderHex,
        fontSize: mdFontSize,
        zIndex: 20,
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? selectedBg
          : state.isFocused
          ? hoverBg
          : 'transparent',
        color: state.isSelected ? headingHex : textHex,
        cursor: 'pointer',
        ':active': { backgroundColor: selectedBg },
      }),
      groupHeading: (base) => ({ ...base, color: mutedHex }),
      singleValue: (base) => ({ ...base, color: headingHex }),
      input: (base) => ({ ...base, color: textHex }),
      placeholder: (base) => ({ ...base, color: mutedHex }),
      indicatorSeparator: (base) => ({ ...base, backgroundColor: borderHex }),
      dropdownIndicator: (base) => ({
        ...base,
        color: mutedHex,
        ':hover': { color: textHex },
      }),
      multiValue: (base) => ({ ...base, backgroundColor: hoverBg }),
      multiValueLabel: (base) => ({ ...base, color: textHex }),
      multiValueRemove: (base) => ({
        ...base,
        color: mutedHex,
        ':hover': { backgroundColor: selectedBg, color: textHex },
      }),
    }),
    [surfaceBg, popupBg, hoverBg, selectedBg, borderHex, textHex, mutedHex, headingHex, strongHex, accentHex, mdFontSize]
  );

  const [files, setFiles] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [sources, setSources] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Files that came back as already-processed (409) and await a re-process confirm.
  const [dupFiles, setDupFiles] = useState([]);
  const [dupDialogOpen, setDupDialogOpen] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);

  const hasFiles = files.length > 0;
  const fileType = files[0] ? getFileType(files[0].name) : null;
  // What the FILE would need on its own: an Excel or PDF may hold several
  // accounts, so normally the upload names a bank and the importer works out
  // which account each table belongs to.
  const fileNeedsSource = requiresSource(fileType);
  // ...but a caller that pinned an account already knows the answer. Onboarding
  // does: the account was created two steps earlier. Asking the importer to
  // match it by name instead fails exactly where it hurts most — one brand-new
  // account, no account number on it yet, and a sheet tab called "PESOS" to
  // match against. Single-account mode takes Excel and PDF too, so pinning the
  // account skips the guess entirely.
  const needsSource = fileNeedsSource && !defaultAccountId;

  useEffect(() => {
    fetchAccounts(true).then(setAccounts).catch(() => {});
    fetchSources().then(setSources).catch(() => {});
  }, []);

  // MI-304: the institution catalog, not the sources already present on the
  // user's accounts. The old derivation made this a closed list that was
  // EMPTY for a new user — so a multi-account PDF could not be uploaded at
  // all until some account happened to carry a typed-in source.
  const sourceList = useMemo(
    () => sources.map((s) => ({ value: s.name, label: s.name, id: s.id, source: s })),
    [sources]
  );

  // MI-345: the institutions the user's accounts are at come first, under
  // their own heading, and the rest of the catalog follows, so a bank with no
  // account yet can still be named. With none of their own (a new user), the
  // list is the catalog alone, as before (null).
  const sourceGroups = useMemo(() => {
    const isOwn = (o) => accounts.some((a) =>
      (o.id != null && a.source_id === o.id) ||
      (a.source || '').trim().toLowerCase() === o.value.trim().toLowerCase()
    );
    const own = sourceList.filter(isOwn);
    if (own.length === 0) return null;
    return [
      { label: t('uploader.fields.yourInstitutions'), options: own },
      { label: t('uploader.fields.otherInstitutions'), options: sourceList.filter((o) => !isOwn(o)) },
    ];
  }, [accounts, sourceList, t]);

  // Render the institution's logo beside its name. react-select takes a plain
  // string label by default, which is why this dropdown used to show only an
  // emoji while the account form showed logos — same catalog, two looks.
  const renderSourceOption = useCallback(
    (opt) => (
      <Flex align="center" gap={2}>
        <SourceGlyph source={opt.source} boxSize="18px" />
        <span>{opt.label}</span>
      </Flex>
    ),
    []
  );

  const accountList = useMemo(
    () => accounts.map((a) => ({ value: a.id, label: a.name })),
    [accounts]
  );

  const accountsForSource = useMemo(() => {
    if (!selectedSource) return [];
    return accounts
      .filter((a) =>
        (selectedSource.id != null && a.source_id === selectedSource.id) ||
        (a.source || '').trim().toLowerCase() ===
          selectedSource.value.trim().toLowerCase()
      )
      .map((a) => ({ value: a.id, label: `${a.name}${a.note ? ` (${a.note})` : ''}` }));
  }, [accounts, selectedSource]);

  // What the caller already knows, in the shape each field wants: a CSV is
  // uploaded against an account, a PDF or Excel against the issuing bank. Both
  // are null when no caller passed one, which is every screen but onboarding —
  // so nothing below changes behaviour there.
  //
  // The multi-account narrowing stays empty on purpose: empty means "every
  // account of this bank", the right default for a statement that may well
  // cover more than the one account onboarding knows about.
  const callerDefaults = useMemo(() => {
    const none = { account: null, source: null };
    if (!defaultAccountId) return none;
    const acct = accounts.find((a) => a.id === defaultAccountId);
    if (!acct) return none;
    return {
      account: accountList.find((o) => o.value === acct.id) || null,
      source: sourceList.find((o) => o.id === acct.source_id) || null,
    };
  }, [defaultAccountId, accounts, accountList, sourceList]);

  // Fills an empty field, never overrides a choice the user has made.
  useEffect(() => {
    setSelectedAccount((prev) => prev || callerDefaults.account);
    setSelectedSource((prev) => prev || callerDefaults.source);
  }, [callerDefaults]);

  // A reset returns to what the caller said, not to blank. Clearing the file
  // says nothing about which account the statement is for, so re-asking would
  // be the form forgetting something it was told.
  const resetForm = useCallback(() => {
    setFiles([]);
    setSelectedAccount(callerDefaults.account);
    setSelectedSource(callerDefaults.source);
    setSelectedAccounts([]);
    setPassword('');
  }, [callerDefaults]);

  const onDrop = (accepted) => {
    if (accepted.length === 0) return;

    const baseType = hasFiles ? fileNeedsSource : requiresSource(getFileType(accepted[0].name));
    const compatible = accepted.filter(
      (f) => requiresSource(getFileType(f.name)) === baseType
    );
    if (compatible.length !== accepted.length) {
      toast({
        title: t('uploader.toasts.incompatibleTitle'),
        description: t('uploader.toasts.incompatibleDescription'),
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
    }
    if (compatible.length === 0) return;

    const remaining = MAX_UPLOAD_FILES - files.length;
    if (compatible.length > remaining) {
      toast({
        title: t('uploader.toasts.fileLimitTitle'),
        description: t('uploader.toasts.fileLimitDescription', { max: MAX_UPLOAD_FILES, skipped: compatible.length - remaining }),
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
    }
    const toAdd = compatible.slice(0, Math.max(0, remaining));
    if (toAdd.length === 0) return;
    setFiles((prev) => [...prev, ...toAdd]);
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    multiple: true,
    accept: ACCEPT,
  });

  const removeFile = (idx) => {
    const next = files.filter((_, i) => i !== idx);
    if (next.length === 0) {
      resetForm();
    } else {
      setFiles(next);
    }
  };

  const isReady =
    hasFiles && ((needsSource && selectedSource) || (!needsSource && selectedAccount));

  const buildArgs = useCallback(
    (file) => ({
      accountId: needsSource ? null : selectedAccount?.value,
      source: needsSource ? selectedSource?.value : null,
      file,
      password: password || null,
      accountIds:
        needsSource && selectedAccounts.length > 0
          ? selectedAccounts.map((a) => a.value)
          : null,
      targetStep: 5,
    }),
    [needsSource, selectedAccount, selectedSource, password, selectedAccounts]
  );

  const isDuplicateError = (err) =>
    err?.response?.status === 409 &&
    err?.response?.data?.details?.reason === 'duplicate';

  // The server opens a PDF when it receives it, so a missing or wrong password
  // is refused there and then, and nothing is created for that file.
  const isPasswordError = (err) =>
    err?.response?.data?.details?.reason === 'pdf_password';

  const handleStart = async () => {
    if (!isReady) return;
    setSubmitting(true);

    const results = await Promise.allSettled(
      files.map((file) => processStatement(buildArgs(file)))
    );
    setSubmitting(false);

    // Partition: started OK / already-processed (409) / wrong PDF password /
    // refused by a limit (a 429: the AI budget, MI-354, or a call limit) /
    // other failure.
    const duplicates = [];
    const wrongPassword = [];
    let refusal = null;
    let succeeded = 0;
    let failed = 0;
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') succeeded += 1;
      else if (isDuplicateError(r.reason)) duplicates.push(files[i]);
      else if (isPasswordError(r.reason)) wrongPassword.push(files[i]);
      else if (limitRefusal(r.reason)) refusal = limitRefusal(r.reason);
      else failed += 1;
    });

    if (refusal) {
      toast({
        title: t('uploader.toasts.limitTitle'),
        description: refusal,
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    }

    if (succeeded > 0 || failed > 0) {
      toast({
        title: failed === 0 ? t('uploader.toasts.startedTitle') : t('uploader.toasts.partialTitle'),
        description:
          failed === 0
            ? t(succeeded === 1 ? 'uploader.toasts.startedDescriptionOne' : 'uploader.toasts.startedDescriptionOther', { count: succeeded })
            : t('uploader.toasts.partialDescription', { succeeded, failed }),
        status: failed === 0 ? 'success' : 'warning',
        duration: 5000,
        isClosable: true,
      });
    }

    if (wrongPassword.length > 0) {
      toast({
        title: t('uploader.toasts.passwordTitle'),
        description: t(
          wrongPassword.length === 1 ? 'uploader.toasts.passwordDescriptionOne' : 'uploader.toasts.passwordDescriptionOther',
          { count: wrongPassword.length }
        ),
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    }

    if (duplicates.length > 0) {
      // Already processed — ask before forcing a fresh re-parse.
      setDupFiles(duplicates);
      setDupDialogOpen(true);
    } else if (wrongPassword.length > 0) {
      // Keep the form open with only the files that were refused, so the
      // password can be corrected and those files sent again.
      setFiles(wrongPassword);
      if (succeeded > 0 && onUploadStarted) onUploadStarted();
    } else {
      resetForm();
      if (onUploadStarted) onUploadStarted();
    }
  };

  const handleConfirmReprocess = async () => {
    setReprocessing(true);
    const results = await Promise.allSettled(
      dupFiles.map((file) => processStatement({ ...buildArgs(file), force: true }))
    );
    setReprocessing(false);

    const ok = results.filter((r) => r.status === 'fulfilled').length;
    toast({
      title: t('uploader.toasts.reprocessStartedTitle'),
      description: t('uploader.toasts.reprocessStartedDescription', { count: ok }),
      status: ok > 0 ? 'success' : 'warning',
      duration: 5000,
      isClosable: true,
    });

    setDupDialogOpen(false);
    setDupFiles([]);
    resetForm();
    if (onUploadStarted) onUploadStarted();
  };

  if (isPhone) {
    return (
      <PhoneUploadSheet
        inputProps={getInputProps()}
        onPick={open}
        files={files}
        onRemove={removeFile}
        onCancel={resetForm}
        needsSource={needsSource}
        fileType={fileType}
        sources={sourceList}
        sourceGroups={sourceGroups}
        selectedSource={selectedSource}
        onSource={(v) => {
          setSelectedSource(v);
          setSelectedAccounts([]);
        }}
        accountsForSource={accountsForSource}
        selectedAccounts={selectedAccounts}
        onAccounts={setSelectedAccounts}
        accounts={accountList}
        selectedAccount={selectedAccount}
        onAccount={setSelectedAccount}
        password={password}
        onPassword={setPassword}
        isReady={isReady}
        submitting={submitting || reprocessing}
        onUpload={handleStart}
        duplicate={{
          isOpen: dupDialogOpen,
          count: dupFiles.length,
          onConfirm: handleConfirmReprocess,
          // The files that were not duplicates have already started, so the
          // form closes rather than offering them again.
          onCancel: () => {
            setDupDialogOpen(false);
            setDupFiles([]);
            resetForm();
            if (onUploadStarted) onUploadStarted();
          },
        }}
      />
    );
  }

  if (!hasFiles) {
    return (
      <Box
        {...getRootProps()}
        borderWidth="2px"
        borderStyle="dashed"
        borderColor={isDragActive ? 'redesign.accent.blue' : 'redesign.border'}
        bg="redesign.bg.surface"
        borderRadius="xl"
        py={8}
        px={6}
        cursor="pointer"
        transition="border-color 0.15s, background-color 0.15s"
        _hover={{ borderColor: 'redesign.accent.blue' }}
      >
        <input {...getInputProps()} />
        <Flex direction="column" align="center" gap={3}>
          <Flex
            w="48px"
            h="48px"
            borderRadius="full"
            bg="redesign.bg.accent"
            align="center"
            justify="center"
          >
            <Icon as={LuUpload} boxSize="22px" color="redesign.accent.blue" />
          </Flex>
          <Text fontSize="sm" fontWeight={500} color="redesign.heading">
            {isDragActive ? t('uploader.dropZone.dropHere') : t('uploader.dropZone.dropHereOrClick')}
          </Text>
          <Text fontSize="xs" color="redesign.text.muted">
            {t('uploader.dropZone.supportedFormats')}
          </Text>
          <HStack spacing={2} mt={1}>
            {FORMAT_CHIPS.map((chip) => (
              <HStack
                key={chip.labelKey}
                spacing={1}
                px={2}
                py="3px"
                borderRadius="md"
                bg="redesign.bg.card"
              >
                <Icon as={chip.icon} boxSize="12px" color={chip.color} />
                <Text fontSize="2xs" color="redesign.text.muted">{t(`uploader.fileTypes.${chip.labelKey}`)}</Text>
              </HStack>
            ))}
          </HStack>
        </Flex>
      </Box>
    );
  }

  return (
    <>
    <Box
      {...cardSurface}
      p={5}
    >
      <VStack spacing={4} align="stretch">
        <Box>
          <HStack mb={2} spacing={2}>
            <Box
              px={2}
              py="2px"
              borderRadius="md"
              bg="redesign.bg.card"
              textStyle="microLabel"
            >
              {files.length === 1
                ? t('uploader.dropZone.fileSelectedOne', { count: files.length })
                : t('uploader.dropZone.fileSelectedOther', { count: files.length })}
            </Box>
          </HStack>
          <VStack spacing={1} align="stretch">
            {files.map((file, idx) => (
              <Flex
                key={`${file.name}-${idx}`}
                align="center"
                justify="space-between"
                px={3}
                py={2}
                borderRadius="md"
                {...TINT.green}
              >
                <HStack spacing={2} minW={0}>
                  <Icon as={CheckCircleIcon} color="redesign.accent.green" boxSize="14px" />
                  <Text fontSize="sm" color="redesign.text" noOfLines={1}>
                    {file.name}
                  </Text>
                  <Text fontSize="2xs" color="redesign.text.muted" flexShrink={0}>
                    {(file.size / 1024).toFixed(1)} KB
                  </Text>
                </HStack>
                <IconButton
                  icon={<CloseIcon boxSize={2} />}
                  size="xs"
                  variant="ghost"
                  aria-label={t('uploader.dropZone.removeAria', { name: file.name })}
                  onClick={() => removeFile(idx)}
                />
              </Flex>
            ))}
          </VStack>
        </Box>

        <Box
          {...getRootProps()}
          borderWidth="2px"
          borderStyle="dashed"
          borderColor={isDragActive ? 'redesign.accent.blue' : 'redesign.border'}
          bg="redesign.bg.surface"
          borderRadius="lg"
          py={5}
          cursor="pointer"
          transition="border-color 0.15s"
          _hover={{ borderColor: 'redesign.accent.blue' }}
        >
          <input {...getInputProps()} />
          <Text textAlign="center" fontSize="sm" color="redesign.text.muted">
            {isDragActive ? t('uploader.dropZone.dropHere') : t('uploader.dropZone.dropMoreHint')}
          </Text>
        </Box>

        {needsSource ? (
          <>
            <FormControl isRequired>
              <FormLabel fontSize="xs" color="redesign.text.muted" mb={1}>
                {t('uploader.fields.bank')}
              </FormLabel>
              <Select
                options={sourceGroups || sourceList}
                formatOptionLabel={renderSourceOption}
                value={selectedSource}
                onChange={(v) => {
                  setSelectedSource(v);
                  setSelectedAccounts([]);
                }}
                placeholder={t('uploader.fields.bankPlaceholder')}
                styles={reactSelectStyles}
              />
            </FormControl>
            {selectedSource && accountsForSource.length > 0 && (
              <FormControl>
                <FormLabel fontSize="xs" color="redesign.text.muted" mb={1}>
                  {t('uploader.fields.restrictAccounts')}
                </FormLabel>
                <Select
                  isMulti
                  options={accountsForSource}
                  value={selectedAccounts}
                  onChange={setSelectedAccounts}
                  placeholder={t('uploader.fields.allAccounts')}
                  styles={reactSelectStyles}
                />
              </FormControl>
            )}
          </>
        ) : (
          <FormControl isRequired>
            <FormLabel fontSize="xs" color="redesign.text.muted" mb={1}>
              {t('uploader.fields.account')}
            </FormLabel>
            <Select
              options={accountList}
              value={selectedAccount}
              onChange={setSelectedAccount}
              placeholder={t('uploader.fields.accountPlaceholder')}
              styles={reactSelectStyles}
            />
          </FormControl>
        )}

        {fileType === 'pdf' && (
          <FormControl>
            <FormLabel fontSize="xs" color="redesign.text.muted" mb={1}>
              {t('uploader.fields.password')}
            </FormLabel>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              size="sm"
              placeholder={t('uploader.fields.passwordPlaceholder')}
            />
          </FormControl>
        )}

        <Button
          variant="primary"
          size="md"
          w="100%"
          onClick={handleStart}
          isDisabled={!isReady}
          isLoading={submitting}
        >
          {files.length > 1 ? t('uploader.buttons.process', { count: files.length }) : t('uploader.buttons.startProcessing')}
        </Button>
      </VStack>
    </Box>
    <ConfirmationDialog
      isOpen={dupDialogOpen}
      onClose={() => {
        setDupDialogOpen(false);
        setDupFiles([]);
      }}
      onConfirm={handleConfirmReprocess}
      title={t('uploader.duplicate.title')}
      message={t(
        dupFiles.length === 1
          ? 'uploader.duplicate.messageOne'
          : 'uploader.duplicate.messageOther',
        { count: dupFiles.length }
      )}
      confirmText={t('uploader.duplicate.confirm')}
      showWarningIcon
      isLoading={reprocessing}
    />
    </>
  );
}
